// client/src/components/BidEstimation/VendorQuotes/VendorQuotesTab.js
import React, { useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { CircularProgress } from '@material-ui/core'
import axios from 'axios'
import QuoteUploadModal from './QuoteUploadModal'
import QuoteReviewTable from './QuoteReviewTable'
import ManualEntryModal from './ManualEntryModal'
import BidEstimationHeader from '../shared/BidEstimationHeader'
import ContextButton from '../shared/ContextButton'
import ContextDrawer from '../ProjectDashboard/ContextDrawer/ContextDrawer'

const STATUS_PILL = {
  parsed:   { label: 'Parsed',   className: 'pill-neutral' },
  reviewed: { label: 'Reviewed', className: 'pill-info' },
  imported: { label: 'Imported', className: 'pill-ok' },
  failed:   { label: 'Failed',   className: 'pill-risk' }
}

const fmt = (v) => (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
const getQuoteIdFromSearch = (search) => new URLSearchParams(search || '').get('quoteId')

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
)

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
)

export default function VendorQuotesTab ({ projectId, onOpenContextDrawer }) {
  const history = useHistory()
  const location = useLocation()
  const [quotes, setQuotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [manualEntryOpen, setManualEntryOpen] = useState(false)
  const [reviewQuoteId, setReviewQuoteId] = useState(() => getQuoteIdFromSearch(location.search))
  const [modules, setModules] = useState([])
  const [moduleFilter, setModuleFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [uploadType, setUploadType] = useState('quote')
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [contextDrawerOpen, setContextDrawerOpen] = useState(false)
  const [contextParentFilter, setContextParentFilter] = useState(null)

  const openQuoteReview = (quoteId) => {
    if (!quoteId) return
    setReviewQuoteId(quoteId)
    history.push(`/bid-estimation/${projectId}/vendor-quotes?quoteId=${quoteId}`)
  }

  const closeQuoteReview = () => {
    setReviewQuoteId(null)
    fetchQuotes()
    if (getQuoteIdFromSearch(location.search)) {
      history.replace(`/bid-estimation/${projectId}/vendor-quotes`)
    }
  }

  const handleOpenContextDrawer = (filter) => {
    if (onOpenContextDrawer) {
      onOpenContextDrawer(filter)
    } else {
      setContextParentFilter(filter)
      setContextDrawerOpen(true)
    }
  }

  const fetchQuotes = async () => {
    setLoading(true)
    try {
      const params = {}
      if (moduleFilter !== 'all') params.moduleId = moduleFilter
      if (typeFilter !== 'all') params.documentType = typeFilter
      const { data } = await axios.get(`/api/bid-estimation/vendor-quotes/project/${projectId}`, { params })
      setQuotes(data)
    } catch (e) {
      console.error('Failed to fetch quotes:', e)
    }
    setLoading(false)
  }

  useEffect(() => {
    if (projectId) {
      fetchQuotes()
      axios.get(`/api/bid-estimation/projects/${projectId}/modules`).then(res => {
        setModules(res.data || [])
      }).catch(() => {})
    }
  }, [projectId])

  useEffect(() => {
    setReviewQuoteId(getQuoteIdFromSearch(location.search))
  }, [location.search])

  useEffect(() => {
    if (projectId) fetchQuotes()
  }, [moduleFilter, typeFilter])

  const handleUploadComplete = (newQuote) => {
    setUploadOpen(false)
    fetchQuotes()
    if (newQuote?._id) {
      openQuoteReview(newQuote._id)
    }
  }

  const handleDelete = async (quoteId) => {
    try {
      await axios.delete(`/api/bid-estimation/vendor-quotes/${quoteId}`)
      setDeleteConfirm(null)
      fetchQuotes()
    } catch (e) {
      console.error('Delete failed:', e)
      setDeleteConfirm(null)
    }
  }

  if (reviewQuoteId) {
    return (
      <QuoteReviewTable
        quoteId={reviewQuoteId}
        projectId={projectId}
        onBack={closeQuoteReview}
      />
    )
  }

  return (
    <div className="hmi-page" style={{ position: 'fixed', inset: 0, zIndex: 1200, overflow: 'auto' }}>
      <BidEstimationHeader
        breadcrumbs={[
          { label: 'Bid Estimation', to: '/bid-estimation' },
          ...(projectId ? [{ label: 'Project', to: `/bid-estimation/${projectId}` }] : []),
          { label: 'Vendor Docs' }
        ]}
        title="Vendor Docs"
        actions={(
          <>
            <button className="hmi-btn hmi-btn-primary" onClick={() => { setUploadType('quote'); setUploadOpen(true) }}>
              + Upload Quote
            </button>
            <button className="hmi-btn hmi-btn-primary" onClick={() => { setUploadType('invoice'); setUploadOpen(true) }}>
              + Upload Invoice
            </button>
            <button className="hmi-btn hmi-btn-outline" onClick={() => setManualEntryOpen(true)}>
              Manual Entry
            </button>
          </>
        )}
      />

      <div className="hmi-body">
        {/* Module filter */}
        {modules.length > 0 && (
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', display: 'block', marginBottom: 4 }}>
              Module
            </label>
            <select
              value={moduleFilter}
              onChange={e => setModuleFilter(e.target.value)}
              style={{
                height: 34, padding: '0 28px 0 10px', borderRadius: 6,
                border: '1px solid var(--line-2)', background: 'var(--bg-panel)',
                fontSize: 13, fontFamily: 'var(--f-sans, var(--font-ui))',
                color: 'var(--ink)', cursor: 'pointer', outline: 'none',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1l4 4 4-4\' stroke=\'%23999\' stroke-width=\'1.5\' fill=\'none\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center'
              }}
            >
              <option value="all">All Modules</option>
              {modules.map(m => (
                <option key={m._id} value={m._id}>{m.scope}</option>
              ))}
            </select>
          </div>
        )}

        {/* Type filter */}
        <div style={{ marginTop: 8 }}>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', display: 'block', marginBottom: 4 }}>
            Type
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            {['all', 'quote', 'invoice'].map(t => (
              <button
                key={t}
                className={`hmi-btn hmi-btn-sm ${typeFilter === t ? 'hmi-btn-primary' : 'hmi-btn-outline'}`}
                onClick={() => setTypeFilter(t)}
                style={{ textTransform: 'capitalize' }}
              >
                {t === 'all' ? 'All' : t + 's'}
              </button>
            ))}
          </div>
        </div>

        {/* Quotes table */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><CircularProgress /></div>
        ) : quotes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)', fontSize: 13 }}>
            No vendor quotes uploaded yet.
          </div>
        ) : (
          <div className="hmi-panel">
            <table className="hmi-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th style={{ textAlign: 'center' }}>Type</th>
                  <th>Module</th>
                  <th>Quote #</th>
                  <th>Date</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'center' }}>Items</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'center', width: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map(q => {
                  const sp = STATUS_PILL[q.status] || STATUS_PILL.parsed
                  return (
                    <tr key={q._id} style={{ cursor: 'pointer' }} onClick={() => openQuoteReview(q._id)}>
                      <td style={{ fontWeight: 550 }}>{q.vendor || 'Unknown'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`pill ${q.documentType === 'invoice' ? 'pill-info' : 'pill-neutral'}`}>
                          <span className="dot" />{q.documentType === 'invoice' ? 'Invoice' : 'Quote'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--ink-3)' }}>
                        {q.moduleId ? (modules.find(m => String(m._id) === String(q.moduleId))?.scope || '\u2014') : '\u2014'}
                      </td>
                      <td style={{ fontFamily: 'var(--f-mono, var(--font-mono))', fontSize: 12 }}>
                        {q.quoteNumber || '\u2014'}
                      </td>
                      <td>{q.quoteDate ? new Date(q.quoteDate).toLocaleDateString() : '\u2014'}</td>
                      <td className="num">{fmt(q.total)}</td>
                      <td style={{ textAlign: 'center', fontFamily: 'var(--f-mono, var(--font-mono))', fontSize: 12 }}>
                        {q.lineItemCount || 0}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`pill ${sp.className}`}><span className="dot" />{sp.label}</span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                          <ContextButton
                            parentType="vendor_quote"
                            parentId={q._id}
                            parentLabel={q.vendor || q.name || 'Quote'}
                            onOpenDrawer={handleOpenContextDrawer}
                          />
                          <button
                            className="hmi-btn hmi-btn-ghost hmi-btn-sm"
                            style={{ width: 28, height: 26, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)' }}
                            title="Review"
                            onClick={(e) => { e.stopPropagation(); openQuoteReview(q._id) }}
                          >
                            <EyeIcon />
                          </button>
                          <button
                            className="hmi-btn hmi-btn-ghost hmi-btn-sm"
                            style={{ width: 28, height: 26, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)' }}
                            title="Delete"
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirm(q) }}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1300,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.3)'
        }} onClick={() => setDeleteConfirm(null)}>
          <div style={{
            background: 'var(--bg-panel)', borderRadius: 12, padding: 24,
            minWidth: 340, maxWidth: 400, boxShadow: '0 8px 24px rgba(30,40,35,0.16)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>Delete Quote</div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 20 }}>
              Delete vendor quote from {deleteConfirm.vendor || 'Unknown'}? This cannot be undone.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="hmi-btn hmi-btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button
                className="hmi-btn"
                style={{ background: 'var(--risk)', color: '#fff', borderColor: 'var(--risk)' }}
                onClick={() => handleDelete(deleteConfirm._id)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <QuoteUploadModal
        open={uploadOpen}
        projectId={projectId}
        modules={modules}
        documentType={uploadType}
        onClose={() => setUploadOpen(false)}
        onComplete={handleUploadComplete}
      />

      <ManualEntryModal
        open={manualEntryOpen}
        projectId={projectId}
        modules={modules}
        onClose={() => setManualEntryOpen(false)}
        onComplete={handleUploadComplete}
      />

      {!onOpenContextDrawer && (
        <ContextDrawer
          open={contextDrawerOpen}
          onClose={() => setContextDrawerOpen(false)}
          projectId={projectId}
          initialTab="attention"
          parentFilter={contextParentFilter}
        />
      )}
    </div>
  )
}
